import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { GitCompare, Users, Search, ArrowRight } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { usePageTitle } from '@/hooks/use-page-title'
import { useI18n } from '@/i18n'
import {useServices} from '@/services/context'
import {SectionPageHeader} from '@/components/shared/SectionPageHeader'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Badge, BadgeVariant } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {ErrorBanner} from '@/components/ui/error-banner'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import type { Identity, IdentityListItem } from '@/types/identity'
import type { EffectiveAccess } from '@/types/effective-access'
import { formatDateOnly } from '@/lib/date-utils'
import {EmptyState} from '@/components/shared/EmptyState'
import {EntityLink as EntityLinkChip} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {SmallText} from '@/components/shared/SmallText'
import {DetailGrid} from '@/components/shared/DetailGrid'

export function CompareGrants() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.analytics.comparePageTitle)
    const [searchParams, setSearchParams] = useSearchParams()
    const identityAId = searchParams.get('a')
    const identityBId = searchParams.get('b')
    const [identityASearchQuery, setIdentityASearchQuery] = useState('')
    const [identityBSearchQuery, setIdentityBSearchQuery] = useState('')

    const setIdentityAId = (id: string | null) => {
        setSearchParams(params => {
            if (id) {
                params.set('a', id)
            } else {
                params.delete('a')
            }
            return params
        })
    }

    const setIdentityBId = (id: string | null) => {
        setSearchParams(params => {
            if (id) {
                params.set('b', id)
            } else {
                params.delete('b')
            }
            return params
        })
    }

    // Search for identity A
    const { data: identityASearchData, isLoading: identityALoading } = useQuery({
        queryKey: ['identities', 'search', identityASearchQuery],
         
        queryFn: () => services.identities.list({q: identityASearchQuery, size: 10}),
        
    })

    // Search for identity B
    const { data: identityBSearchData, isLoading: identityBLoading } = useQuery({
        queryKey: ['identities', 'search', identityBSearchQuery],
         
        queryFn: () => services.identities.list({q: identityBSearchQuery, size: 10}),
        
    })

    const identityAOptions: ComboboxOption[] = (identityASearchData?.content || []).map((i: IdentityListItem) => ({
        value: i.id,
        label: i.party.displayName || i.identityType,
        description: i.identityType,
    }))

    const identityBOptions: ComboboxOption[] = (identityBSearchData?.content || []).map((i: IdentityListItem) => ({
        value: i.id,
        label: i.party.displayName || i.identityType,
        description: i.identityType,
    }))

    // Fetch selected identities for display
    const { data: identityA } = useQuery({
        queryKey: ['identities', identityAId],
        queryFn: () => services.identities.detail(identityAId!),
        enabled: !!identityAId,
    })

    const { data: identityB } = useQuery({
        queryKey: ['identities', identityBId],
        queryFn: () => services.identities.detail(identityBId!),
        enabled: !!identityBId,
    })

    // Fetch comparison
    const { data: comparison, isLoading: comparisonLoading, error: comparisonError } = useQuery({
        queryKey: ['identity-grants-compare', identityAId, identityBId],
        queryFn: () => services.identities.compareGrants(identityAId!, identityBId!),
        enabled: !!identityAId && !!identityBId,
    })

    const getIdentityDisplayName = (identity: Identity | undefined): string => {
         
        if (!identity) return i18n.common.unknown as string
        return identity.party.displayName || identity.identityType
    }

    const renderGrantRow = (grant: EffectiveAccess) => (
        <TableRow key={grant.accessId}>
            <TableCell>
                <EntityLinkChip
                    value={grant.enforcementObject}
                    testId={`link-enforcement-object-${grant.accessId}`}
                />
            </TableCell>
            <TableCell>
                <EntityLinkChip
                    value={grant.targetSystem}
                    testId={`link-target-system-${grant.accessId}`}
                />
            </TableCell>
            <TableCell>
                <Badge variant="outline">{grant.operation}</Badge>
            </TableCell>
            <TableCell>
                {grant.permanent ? (
                    <Badge variant="secondary">{i18n.common.permanent}</Badge>
                ) : grant.validTo ? (
                    formatDateOnly(grant.validTo)
                ) : (
                    '-'
                )}
            </TableCell>
        </TableRow>
    )

    return (
        <div>
            {/* Header */}
            <SectionPageHeader
                icon={GitCompare}
                title={i18n.analytics.compareAccess as string}
                description={i18n.analytics.compareAccessDesc as string}
            />

            {/* Identity Selection */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle data-testid="card-title-select-identities">{i18n.analytics.selectIdentitiesLabel}</CardTitle>
                    <CardDescription>
                        {i18n.analytics.selectIdentitiesDesc}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <InlineRow wrap gap="md">
                        <Stack className="flex-1 min-w-[200px]">
                            <SmallText className="font-medium">{i18n.analytics.identityA}</SmallText>
                            <Combobox
                                data-testid="combobox-identity-a"
                                options={identityAOptions}
                                value={identityAId}
                                onValueChange={setIdentityAId}
                                onSearch={setIdentityASearchQuery}
                                placeholder={i18n.analytics.searchForIdentity as string}
                                emptyMessage={i18n.analytics.noIdentitiesFoundSearch as string}
                                isLoading={identityALoading}
                                selectedLabel={getIdentityDisplayName(identityA)}
                            />
                        </Stack>
                        <InlineRow className="justify-center pt-6">
                            <Icon icon={ArrowRight} size="lg" muted/>
                        </InlineRow>
                        <Stack className="flex-1 min-w-[200px]">
                            <SmallText className="font-medium">{i18n.analytics.identityB}</SmallText>
                            <Combobox
                                data-testid="combobox-identity-b"
                                options={identityBOptions}
                                value={identityBId}
                                onValueChange={setIdentityBId}
                                onSearch={setIdentityBSearchQuery}
                                placeholder={i18n.analytics.searchForIdentity as string}
                                emptyMessage={i18n.analytics.noIdentitiesFoundSearch as string}
                                isLoading={identityBLoading}
                                selectedLabel={getIdentityDisplayName(identityB)}
                            />
                        </Stack>
                    </InlineRow>
                </CardContent>
            </Card>

            {/* Comparison Results */}
            {identityAId && identityBId && (
                <>
                    {comparisonLoading && (
                        <MutedText>{i18n.analytics.loadingComparison}</MutedText>
                    )}

                    <ErrorBanner error={comparisonError}/>

                    {comparison && (
                        <>
                            {/* Stats */}
                            <DetailGrid columns={3} className="mb-6">
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardDescription>{i18n.analytics.commonGrants}</CardDescription>
                                        <CardTitle className="text-2xl text-status-success" data-testid="card-title-common-count">{comparison.commonCount}</CardTitle>
                                    </CardHeader>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardDescription>{i18n.analytics.onlyIn({name: getIdentityDisplayName(identityA)})}</CardDescription>
                                        <CardTitle className="text-2xl text-status-info" data-testid="card-title-only-in-a-count">{comparison.onlyInACount}</CardTitle>
                                    </CardHeader>
                                </Card>
                                <Card>
                                    <CardHeader className="pb-2">
                                        <CardDescription>{i18n.analytics.onlyIn({name: getIdentityDisplayName(identityB)})}</CardDescription>
                                        <CardTitle className="text-2xl text-status-warning" data-testid="card-title-only-in-b-count">{comparison.onlyInBCount}</CardTitle>
                                    </CardHeader>
                                </Card>
                            </DetailGrid>

                            {/* Common Grants */}
                            {comparison.common.length > 0 && (
                                <Card className="mb-6">
                                    <CardHeader>
                                        <CardTitle className="items-center gap-2" data-testid="card-title-common-grants">
                                            <Badge variant={BadgeVariant.SUCCESS}>{comparison.commonCount}</Badge>
                                            {i18n.analytics.commonGrants}
                                        </CardTitle>
                                        <CardDescription>
                                            {i18n.analytics.commonGrantsDesc}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <Table className="table-fixed">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[40%]" data-testid="col-common-enforcement-object">{i18n.analytics.enforcementObject}</TableHead>
                                                    <TableHead className="w-[25%]" data-testid="col-common-target-system">{i18n.analytics.targetSystem}</TableHead>
                                                    <TableHead className="w-[15%]" data-testid="col-common-operation">{i18n.common.operation}</TableHead>
                                                    <TableHead className="w-[20%]" data-testid="col-common-valid-until">{i18n.common.validUntil}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {comparison.common.map(renderGrantRow)}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Only in A */}
                            {comparison.onlyInA.length > 0 && (
                                <Card className="mb-6">
                                    <CardHeader>
                                        <CardTitle className="items-center gap-2" data-testid="card-title-only-in-a">
                                            <Badge variant="outline" className="bg-status-info-subtle text-status-info">{comparison.onlyInACount}</Badge>
                                            {i18n.analytics.onlyIn({name: getIdentityDisplayName(identityA)})}
                                        </CardTitle>
                                        <CardDescription>
                                            {i18n.analytics.onlyInDesc({name: getIdentityDisplayName(identityA)})}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <Table className="table-fixed">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[40%]" data-testid="col-only-a-enforcement-object">{i18n.analytics.enforcementObject}</TableHead>
                                                    <TableHead className="w-[25%]" data-testid="col-only-a-target-system">{i18n.analytics.targetSystem}</TableHead>
                                                    <TableHead className="w-[15%]" data-testid="col-only-a-operation">{i18n.common.operation}</TableHead>
                                                    <TableHead className="w-[20%]" data-testid="col-only-a-valid-until">{i18n.common.validUntil}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {comparison.onlyInA.map(renderGrantRow)}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            )}

                            {/* Only in B */}
                            {comparison.onlyInB.length > 0 && (
                                <Card className="mb-6">
                                    <CardHeader>
                                        <CardTitle className="items-center gap-2" data-testid="card-title-only-in-b">
                                            <Badge variant="outline" className="bg-status-warning-subtle text-status-warning">{comparison.onlyInBCount}</Badge>
                                            {i18n.analytics.onlyIn({name: getIdentityDisplayName(identityB)})}
                                        </CardTitle>
                                        <CardDescription>
                                            {i18n.analytics.onlyInDesc({name: getIdentityDisplayName(identityB)})}
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <Table className="table-fixed">
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead className="w-[40%]" data-testid="col-only-b-enforcement-object">{i18n.analytics.enforcementObject}</TableHead>
                                                    <TableHead className="w-[25%]" data-testid="col-only-b-target-system">{i18n.analytics.targetSystem}</TableHead>
                                                    <TableHead className="w-[15%]" data-testid="col-only-b-operation">{i18n.common.operation}</TableHead>
                                                    <TableHead className="w-[20%]" data-testid="col-only-b-valid-until">{i18n.common.validUntil}</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {comparison.onlyInB.map(renderGrantRow)}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            )}

                            {/* No differences */}
                            {comparison.commonCount === 0 && comparison.onlyInACount === 0 && comparison.onlyInBCount === 0 && (
                                <Card>
                                    <CardContent className="pt-6">
                                        <EmptyState
                                            icon={Users}
                                            title={String(i18n.analytics.neitherHasGrants)}
                                        />
                                    </CardContent>
                                </Card>
                            )}
                        </>
                    )}
                </>
            )}

            {/* Initial state */}
            {(!identityAId || !identityBId) && (
                <Card>
                    <CardContent className="pt-6">
                        <EmptyState
                            icon={Search}
                            title={String(i18n.analytics.selectTwoIdentities)}
                        />
                    </CardContent>
                </Card>
            )}
        </div>
    )
}
